import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import View from "../../assets/images/Group.svg";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall, simplePUTCall, getWithAuthCall } from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import { DateDDMMYYYY } from "../../sharedComponent/common";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { PageHead, StatusBadge } from "./ffUi";
import {
  toDateInput,
  todayInput,
  useVehicles,
  vehicleNameById,
  labelFor,
  formatValue,
} from "./ffHelpers";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const money = (n) =>
  n || n === 0 ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "--";

const STEP_STATUSES = ["pending", "pass", "fail", "na"];

const todayStr = () => todayInput(); // FF-06: local calendar date
const iso = (d) => toDateInput(d);

// Quick-preset date windows for the utilization audit.
const presetRange = (preset) => {
  const now = new Date();
  if (preset === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // Sunday of current week
    return { from_date: iso(start), to_date: iso(now) };
  }
  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from_date: iso(start), to_date: iso(now) };
  }
  return { from_date: "", to_date: "" }; // lifetime / custom-cleared
};

const pct = (v) =>
  v || v === 0 ? `${Number(v).toFixed(1)}%` : "-";

const FleetAudit = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState([]);

  // U-01 / U-02: shared vehicle list; audit checks return only vehicle_id
  const vehicles = useVehicles();
  const vehicleName = (id) => vehicleNameById(vehicles, id);

  // create modal
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    audit_date: todayStr(),
    period_from: "",
    period_to: "",
    notes: "",
  });

  // detail modal
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [steps, setSteps] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingStep, setSavingStep] = useState(null);

  // checks modal (generic — GL recon / 40% rule)
  const [showCheck, setShowCheck] = useState(false);
  const [checkTitle, setCheckTitle] = useState("");
  const [checkRows, setCheckRows] = useState([]);
  const [checkLoading, setCheckLoading] = useState(false);

  // utilization modal
  const [showUtil, setShowUtil] = useState(false);
  const [utilLoading, setUtilLoading] = useState(false);
  const [utilResult, setUtilResult] = useState(null);
  const [utilForm, setUtilForm] = useState({
    preset: "lifetime", // lifetime | week | month | custom
    from_date: "",
    to_date: "",
    scope: "configured", // configured | all
    threshold_pct: 60,
  });

  // cpk-variance modal
  const [showCpk, setShowCpk] = useState(false);
  const [cpkLoading, setCpkLoading] = useState(false);
  const [cpkResult, setCpkResult] = useState(null);
  const [cpkForm, setCpkForm] = useState({ investigate_pct: 10, halt_pct: 20 });

  useEffect(() => {
    getAudits();
  }, []);

  const getAudits = () => {
    setLoading(true);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_AUDIT_LIST,
      JSON.stringify({ page: 1, page_limit: 50 })
    )
      .then((data) => {
        setAudits((data?.success || data?.result) ? data?.data || [] : []);
      })
      .catch((err) => {
        console.log("audit list err", err);
        setAudits([]);
      })
      .finally(() => setLoading(false));
  };

  const createAudit = (e) => {
    e.preventDefault();
    // FF-11: period is mandatory and must be ordered
    if (!createForm.period_from || !createForm.period_to)
      return notifyError(t("Period from and period to are required"));
    if (createForm.period_from > createForm.period_to)
      return notifyError(t("Period from cannot be after period to"));
    setCreating(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_AUDIT_CREATE, JSON.stringify(createForm))
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Audit created with 13 steps");
          setShowCreate(false);
          getAudits();
          if (data?.data?.audit_id) openDetail(data.data.audit_id);
        } else {
          notifyError(data?.message || "Unable to create audit");
        }
      })
      .catch((err) => {
        console.log("audit create err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setCreating(false));
  };

  const openDetail = (auditId) => {
    setShowDetail(true);
    setDetail(null);
    setSteps([]);
    setDetailLoading(true);
    getWithAuthCall(ApiConfig.FLEET_FINANCE_AUDIT_SINGLE + auditId)
      .then((data) => {
        if (data?.success || data?.result) {
          const d = data?.data || {};
          setDetail(d.audit || d.header || d);
          setSteps(d.steps || d.lines || []);
        } else {
          notifyError(data?.message || "Unable to load audit");
        }
      })
      .catch((err) => {
        console.log("audit single err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setDetailLoading(false));
  };

  const updateStepField = (stepNo, field, value) => {
    setSteps((prev) =>
      prev.map((s) =>
        (s.step_no ?? s.step_number) === stepNo ? { ...s, [field]: value } : s
      )
    );
  };

  const saveStep = (step) => {
    const stepNo = step.step_no ?? step.step_number;
    setSavingStep(stepNo);
    const body = JSON.stringify({
      audit_id: detail.audit_id,
      step_no: stepNo,
      status: step.status || "pending",
      finding: step.finding || "",
    });
    simplePUTCall(ApiConfig.FLEET_FINANCE_AUDIT_STEP_UPDATE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || `Step ${stepNo} updated`);
        } else {
          notifyError(data?.message || "Unable to update step");
        }
      })
      .catch((err) => {
        console.log("step update err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSavingStep(null));
  };

  const runCheck = (url, title, body) => {
    setCheckTitle(title);
    setCheckRows([]);
    setCheckLoading(true);
    setShowCheck(true);
    simplePostCall(url, JSON.stringify(body || {}))
      .then((data) => {
        if (data?.success || data?.result) {
          const d = data?.data;
          setCheckRows(Array.isArray(d) ? d : d?.rows || d?.vehicles || (d ? [d] : []));
        } else {
          notifyError(data?.message || "Unable to run check");
          setCheckRows([]);
        }
      })
      .catch((err) => {
        console.log("audit check err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setCheckLoading(false));
  };

  // ===== Utilization (time-based + config-driven expected km) =====
  const changeUtilPreset = (preset) => {
    setUtilForm((prev) => ({
      ...prev,
      preset,
      ...(preset === "custom" ? {} : presetRange(preset)),
    }));
  };

  const runUtilization = () => {
    setUtilLoading(true);
    setUtilResult(null);
    const body = { scope: utilForm.scope };
    if (utilForm.from_date) body.from_date = utilForm.from_date;
    if (utilForm.to_date) body.to_date = utilForm.to_date;
    if (utilForm.threshold_pct !== "" && utilForm.threshold_pct != null)
      body.threshold_pct = Number(utilForm.threshold_pct);
    simplePostCall(ApiConfig.FLEET_FINANCE_AUDIT_UTILIZATION, JSON.stringify(body))
      .then((data) => {
        if (data?.success || data?.result) {
          setUtilResult(data);
        } else {
          notifyError(data?.message || "Unable to run utilization audit");
          setUtilResult(null);
        }
      })
      .catch((err) => {
        console.log("utilization err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setUtilLoading(false));
  };

  // ===== CPK Variance (per vehicle + category-wise by_type) =====
  const runCpk = () => {
    setCpkLoading(true);
    setCpkResult(null);
    const body = {
      investigate_pct: Number(cpkForm.investigate_pct) || 0,
      halt_pct: Number(cpkForm.halt_pct) || 0,
    };
    simplePostCall(ApiConfig.FLEET_FINANCE_AUDIT_CPK_VARIANCE, JSON.stringify(body))
      .then((data) => {
        if (data?.success || data?.result) {
          setCpkResult(data);
        } else {
          notifyError(data?.message || "Unable to run CPK variance");
          setCpkResult(null);
        }
      })
      .catch((err) => {
        console.log("cpk variance err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setCpkLoading(false));
  };

  const checks = [
    {
      title: t("GL Reconciliation"),
      run: () =>
        runCheck(ApiConfig.FLEET_FINANCE_AUDIT_GL_RECONCILIATION, t("GL Reconciliation"), {
          variance_threshold: 1000,
        }),
    },
    {
      title: t("40% Rule"),
      run: () =>
        runCheck(ApiConfig.FLEET_FINANCE_AUDIT_FORTY_PERCENT_RULE, t("40% Rule (replacement queue)"), {}),
    },
    {
      title: t("CPK Variance"),
      run: () => {
        setCpkResult(null);
        setShowCpk(true);
      },
    },
    {
      title: t("Utilization"),
      run: () => {
        setUtilResult(null);
        setShowUtil(true);
      },
    },
  ];

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Audit">
        <PageHead
          title={t("Audit & Right-Sizing")}
          subtitle={t("13-step audit protocol with automated fleet checks")}
        >
          <button className="cx-btn-2" onClick={() => setShowCreate(true)}>
            + {t("Create Audit")}
          </button>
        </PageHead>
        <div className="d-flex flex-wrap" style={{ gap: 10, marginBottom: 14 }}>
          {checks.map((c) => (
            <button key={c.title} className="cx-btn-1" onClick={c.run}>
              {c.title}
            </button>
          ))}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="main-master-wrapper">
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Audit ID")}</th>
                      <th>{t("Audit Date")}</th>
                      <th>{t("Period From")}</th>
                      <th>{t("Period To")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Notes")}</th>
                      <th>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {audits && audits.length > 0 ? (
                      audits.map((a) => (
                        <tr key={a.audit_id}>
                          <td>{a.audit_id}</td>
                          <td>{a.audit_date ? DateDDMMYYYY(a.audit_date) : "-"}</td>
                          <td>{a.period_from ? DateDDMMYYYY(a.period_from) : "-"}</td>
                          <td>{a.period_to ? DateDDMMYYYY(a.period_to) : "-"}</td>
                          <td>
                            <StatusBadge value={a.status} />
                          </td>
                          <td>{a.notes || "-"}</td>
                          <td>
                            <img
                              src={View}
                              alt="view"
                              style={{ cursor: "pointer" }}
                              onClick={() => openDetail(a.audit_id)}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create audit modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered className="common-model">
        <Form onSubmit={createAudit}>
          <Modal.Header closeButton>
            <Modal.Title>{t("Create Audit")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">{t("Audit Date")}</Form.Label>
                <Form.Control
                  type="date"
                  value={createForm.audit_date}
                  onChange={(e) => setCreateForm((p) => ({ ...p, audit_date: e.target.value }))}
                />
              </div>
              {/* FF-11: period dates are required */}
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Period From")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="date"
                  max={createForm.period_to || undefined}
                  value={createForm.period_from}
                  onChange={(e) => setCreateForm((p) => ({ ...p, period_from: e.target.value }))}
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Period To")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="date"
                  min={createForm.period_from || undefined}
                  value={createForm.period_to}
                  onChange={(e) => setCreateForm((p) => ({ ...p, period_to: e.target.value }))}
                />
              </div>
              <div className="col-md-12 form_input_main">
                <Form.Label className="common-labels">{t("Notes")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={createForm.notes}
                  onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="pop-up-modal-footer">
            <div className="btn-wrapper">
              <button type="button" className="cx-btn-1" onClick={() => setShowCreate(false)}>
                {t("Cancel")}
              </button>
              <button type="submit" className="cx-btn-2" disabled={creating}>
                {creating ? <Spinner animation="border" size="sm" /> : t("Create")}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Audit detail (13 steps) modal */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered size="lg" className="common-model ff-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("Audit Steps")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <Loader />
          ) : (
            <div className="VA_table">
              <table className="table tableAdmin">
                <thead className="tableHead">
                  <tr>
                    <th>#</th>
                    <th>{t("Step")}</th>
                    <th>{t("Status")}</th>
                    <th>{t("Finding")}</th>
                    <th>{t("Action")}</th>
                  </tr>
                </thead>
                <tbody className="tableBody">
                  {steps.length > 0 ? (
                    steps.map((s) => {
                      const stepNo = s.step_no ?? s.step_number;
                      return (
                        <tr key={stepNo}>
                          <td>{stepNo}</td>
                          <td>{s.step_name || s.name || s.description || "-"}</td>
                          <td style={{ minWidth: 120 }}>
                            <Form.Select
                              value={s.status || "pending"}
                              onChange={(e) => updateStepField(stepNo, "status", e.target.value)}
                            >
                              {STEP_STATUSES.map((st) => (
                                <option key={st} value={st}>
                                  {st}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td style={{ minWidth: 200 }}>
                            <Form.Control
                              type="text"
                              value={s.finding || ""}
                              onChange={(e) => updateStepField(stepNo, "finding", e.target.value)}
                            />
                          </td>
                          <td>
                            <button
                              className="cx-btn-1"
                              onClick={() => saveStep(s)}
                              disabled={savingStep === stepNo}
                            >
                              {savingStep === stepNo ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                t("Save")
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5}>
                        <NoDataComp />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Computed check results modal */}
      <Modal show={showCheck} onHide={() => setShowCheck(false)} centered size="lg" className="common-model ff-modal">
        <Modal.Header closeButton>
          <Modal.Title>{checkTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {checkLoading ? (
            <Loader />
          ) : checkRows.length > 0 ? (
            <div className="VA_table">
              <table className="table tableAdmin">
                <thead className="tableHead">
                  <tr>
                    {/* U-03: readable headers */}
                    {Object.keys(checkRows[0]).map((k) => (
                      <th key={k}>{labelFor(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="tableBody">
                  {checkRows.map((row, i) => (
                    <tr key={i}>
                      {/* U-03: Yes/No, money, vehicle names */}
                      {Object.entries(row).map(([k, v]) => (
                        <td key={k}>{formatValue(v, k, vehicles)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoDataComp />
          )}
        </Modal.Body>
      </Modal>

      {/* Utilization audit modal */}
      <Modal
        show={showUtil}
        onHide={() => setShowUtil(false)}
        centered
        size="lg"
        className="common-model ff-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Utilization Audit")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row" style={{ marginBottom: 12 }}>
            <div className="col-md-3 form_input_main">
              <Form.Label className="common-labels">{t("Period")}</Form.Label>
              <Form.Select
                value={utilForm.preset}
                onChange={(e) => changeUtilPreset(e.target.value)}
              >
                <option value="lifetime">{t("Lifetime (odometer)")}</option>
                <option value="week">{t("This Week")}</option>
                <option value="month">{t("This Month")}</option>
                <option value="custom">{t("Custom")}</option>
              </Form.Select>
            </div>
            <div className="col-md-3 form_input_main">
              <Form.Label className="common-labels">{t("From")}</Form.Label>
              <Form.Control
                type="date"
                value={utilForm.from_date}
                disabled={utilForm.preset !== "custom"}
                onChange={(e) =>
                  setUtilForm((p) => ({ ...p, from_date: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3 form_input_main">
              <Form.Label className="common-labels">{t("To")}</Form.Label>
              <Form.Control
                type="date"
                value={utilForm.to_date}
                disabled={utilForm.preset !== "custom"}
                onChange={(e) =>
                  setUtilForm((p) => ({ ...p, to_date: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3 form_input_main">
              <Form.Label className="common-labels">{t("Scope")}</Form.Label>
              <Form.Select
                value={utilForm.scope}
                onChange={(e) =>
                  setUtilForm((p) => ({ ...p, scope: e.target.value }))
                }
              >
                <option value="configured">{t("Configured vehicles")}</option>
                <option value="all">{t("All vehicles")}</option>
              </Form.Select>
            </div>
            <div className="col-md-3 form_input_main">
              <Form.Label className="common-labels">
                {t("Threshold")} (%)
              </Form.Label>
              <Form.Control
                type="number"
                value={utilForm.threshold_pct}
                onChange={(e) =>
                  setUtilForm((p) => ({ ...p, threshold_pct: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3 form_input_main d-flex align-items-end">
              <button
                className="cx-btn-2"
                onClick={runUtilization}
                disabled={utilLoading}
              >
                {utilLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  t("Run Audit")
                )}
              </button>
            </div>
          </div>

          {utilLoading ? (
            <Loader />
          ) : utilResult ? (
            <>
              <div className="d-flex flex-wrap" style={{ gap: 16, marginBottom: 12 }}>
                <span className="ff-badge ff-badge-info">
                  {t("Basis")}: {String(utilResult.basis || "-").replace(/_/g, " ")}
                </span>
                {utilResult.period && (utilResult.period.from_date || utilResult.period.days) ? (
                  <span className="ff-badge ff-badge-muted">
                    {utilResult.period.from_date} → {utilResult.period.to_date} (
                    {utilResult.period.days} {t("days")})
                  </span>
                ) : null}
                <span className="ff-badge ff-badge-muted">
                  {t("Vehicles")}: {utilResult.vehicle_count ?? "-"}
                </span>
                <span className="ff-badge ff-badge-danger">
                  {t("Underutilized")}: {utilResult.underutilized_count ?? 0}
                </span>
              </div>
              {utilResult.vehicles_without_expected_km > 0 ? (
                <div className="ff-badge ff-badge-warning" style={{ marginBottom: 12 }}>
                  {utilResult.vehicles_without_expected_km}{" "}
                  {t(
                    "vehicle(s) have no expected km — set it on the vehicle TCO config or fleet default."
                  )}
                </div>
              ) : null}
              {utilResult.data && utilResult.data.length > 0 ? (
                <div className="VA_table">
                  <table className="table tableAdmin">
                    <thead className="tableHead">
                      <tr>
                        <th>{t("Vehicle")}</th>
                        <th>{t("KM Driven")}</th>
                        <th>{t("Expected KM")}</th>
                        <th>{t("Source")}</th>
                        <th>{t("Utilization %")}</th>
                        <th>{t("Status")}</th>
                      </tr>
                    </thead>
                    <tbody className="tableBody">
                      {utilResult.data.map((r) => (
                        <tr
                          key={r.vehicle_id}
                          style={
                            r.underutilized
                              ? { background: "rgba(220,53,69,0.08)" }
                              : undefined
                          }
                        >
                          <td>{vehicleName(r.vehicle_id)}</td>
                          <td>{money(r.km_driven)}</td>
                          <td>{money(r.expected_km)}</td>
                          <td>
                            <StatusBadge
                              value={String(r.expected_source || "-").replace(/_/g, " ")}
                              variant="muted"
                            />
                          </td>
                          <td style={{ minWidth: 140 }}>
                            <div className="ff-util-bar">
                              <span
                                className="ff-util-bar-fill"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    Number(r.utilization_pct) || 0
                                  )}%`,
                                }}
                              />
                            </div>
                            <small>{pct(r.utilization_pct)}</small>
                          </td>
                          <td>
                            <StatusBadge
                              value={r.underutilized ? "underutilized" : "ok"}
                              variant={r.underutilized ? "danger" : "success"}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <NoDataComp />
              )}
            </>
          ) : (
            <p style={{ opacity: 0.6 }}>
              {t("Choose a period and scope, then run the audit.")}
            </p>
          )}
        </Modal.Body>
      </Modal>

      {/* CPK variance modal (per vehicle + by type) */}
      <Modal
        show={showCpk}
        onHide={() => setShowCpk(false)}
        centered
        size="lg"
        className="common-model ff-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("CPK Variance")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row" style={{ marginBottom: 12 }}>
            <div className="col-md-3 form_input_main">
              <Form.Label className="common-labels">
                {t("Investigate")} (%)
              </Form.Label>
              <Form.Control
                type="number"
                value={cpkForm.investigate_pct}
                onChange={(e) =>
                  setCpkForm((p) => ({ ...p, investigate_pct: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3 form_input_main">
              <Form.Label className="common-labels">{t("Halt")} (%)</Form.Label>
              <Form.Control
                type="number"
                value={cpkForm.halt_pct}
                onChange={(e) =>
                  setCpkForm((p) => ({ ...p, halt_pct: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3 form_input_main d-flex align-items-end">
              <button className="cx-btn-2" onClick={runCpk} disabled={cpkLoading}>
                {cpkLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  t("Run Check")
                )}
              </button>
            </div>
          </div>

          {cpkLoading ? (
            <Loader />
          ) : cpkResult ? (
            <>
              <div className="d-flex flex-wrap" style={{ gap: 16, marginBottom: 12 }}>
                <span className="ff-badge ff-badge-danger">
                  {t("Halt")}: {cpkResult.halt_count ?? 0}
                </span>
                <span className="ff-badge ff-badge-warning">
                  {t("Investigate")}: {cpkResult.investigate_count ?? 0}
                </span>
              </div>

              {/* By Type summary */}
              <div className="vehicleHead" style={{ marginBottom: 8 }}>
                <p style={{ marginBottom: 0 }}>{t("By Vehicle Type")}</p>
              </div>
              {cpkResult.by_type && cpkResult.by_type.length > 0 ? (
                <div className="VA_table" style={{ marginBottom: 20 }}>
                  <table className="table tableAdmin">
                    <thead className="tableHead">
                      <tr>
                        <th>{t("Type")}</th>
                        <th>{t("Vehicles")}</th>
                        <th>{t("CPK")}</th>
                        <th>{t("Planned CPK")}</th>
                        <th>{t("Source")}</th>
                        <th>{t("Variance %")}</th>
                        <th>{t("Action")}</th>
                      </tr>
                    </thead>
                    <tbody className="tableBody">
                      {cpkResult.by_type.map((r) => (
                        <tr key={r.vehicle_type_id}>
                          <td>{r.type || r.vehicle_type_id}</td>
                          <td>{r.vehicle_count ?? "-"}</td>
                          <td>{money(r.cpk)}</td>
                          <td>{money(r.planned_cpk)}</td>
                          <td>
                            <StatusBadge
                              value={String(r.planned_source || "-").replace(/_/g, " ")}
                              variant="muted"
                            />
                          </td>
                          <td>{pct(r.cpk_variance_pct)}</td>
                          <td>
                            <StatusBadge value={r.action} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <NoDataComp />
              )}

              {/* Per-vehicle drill-down */}
              <div className="vehicleHead" style={{ marginBottom: 8 }}>
                <p style={{ marginBottom: 0 }}>{t("Per Vehicle")}</p>
              </div>
              {cpkResult.data && cpkResult.data.length > 0 ? (
                <div className="VA_table">
                  <table className="table tableAdmin">
                    <thead className="tableHead">
                      <tr>
                        <th>{t("Vehicle")}</th>
                        <th>{t("Type")}</th>
                        <th>{t("Cost")}</th>
                        <th>{t("KM")}</th>
                        <th>{t("CPK")}</th>
                        <th>{t("Planned CPK")}</th>
                        <th>{t("Source")}</th>
                        <th>{t("Variance %")}</th>
                        <th>{t("Action")}</th>
                      </tr>
                    </thead>
                    <tbody className="tableBody">
                      {cpkResult.data.map((r) => (
                        <tr key={r.vehicle_id}>
                          <td>{vehicleName(r.vehicle_id)}</td>
                          <td>{r.type || r.vehicle_type_id || "-"}</td>
                          <td>{money(r.cost)}</td>
                          <td>{money(r.km)}</td>
                          <td>{money(r.cpk)}</td>
                          <td>{money(r.planned_cpk)}</td>
                          <td>
                            <StatusBadge
                              value={String(r.planned_source || "-").replace(/_/g, " ")}
                              variant="muted"
                            />
                          </td>
                          <td>{pct(r.cpk_variance_pct)}</td>
                          <td>
                            <StatusBadge value={r.action} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <NoDataComp />
              )}
            </>
          ) : (
            <p style={{ opacity: 0.6 }}>
              {t("Set thresholds and run the check.")}
            </p>
          )}
        </Modal.Body>
      </Modal>
    </motion.div>
  );
};

export default FleetAudit;
